/**
 * Channel dropdowns: assign a space to a channel, and switch the current
 * user's active channel.
 *
 * `ChannelSelectionDropDown` switches which of the user's
 * own channels is "current" for the session.
 */
import { DropDownSelector } from 'component/popup';
import { UtilityText } from 'component/text';
import { useCurrentChannelKey, useSetCurrentChannel } from 'data/admins';
import type { Channel } from 'data/channels';
import { useAllChannels, useMyChannelKeys } from 'data/channels';
import { StyleSheet } from 'react-native';
import { View } from 'react-native-web';
import { useHasCapability } from 'util/admin';
import { fireAndForget } from 'util/hooks';
import { t } from 'util/stdtypes';

export function useMyChannels(): Channel[] {
    const canAccessAllChannels = useHasCapability('channel/access-all-channels');
    const { channels: allChannels } = useAllChannels();
    const defaultChannel = useDefaultChannel();
    const { channelKeys: userMemberships } = useMyChannelKeys();

    if (canAccessAllChannels) {
        return allChannels;
    } else {
        let userChannels = allChannels.filter((channel) => userMemberships?.includes(channel.channelKey));
        if (defaultChannel) {
            userChannels = [...userChannels, defaultChannel];
        }
        return userChannels;
    }
}

// Displays the currently selected channel
/** @public */
export function useMyCurrentChannel(): Channel | null | undefined {
    const myChannels = useMyChannels();
    const { channelKey: currentChannel } = useCurrentChannelKey();
    return myChannels.filter((channel) => channel.channelKey === currentChannel)[0] ?? (myChannels.length > 0 ? myChannels[0] : null);
}

/** @public */
export function useDefaultChannel(): Channel | undefined {
    const { channels: allChannels } = useAllChannels();
    return allChannels.find((channel) => channel.isDefault === true);
}

const ChannelDropDownSelectorStyle = StyleSheet.create({
    channelDropDownStyle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
});

export function ChannelSelectionDropDown() {
    const s = ChannelDropDownSelectorStyle;

    const { setCurrentChannelAsync } = useSetCurrentChannel();
    const currentChannel = useMyCurrentChannel();
    const userChannels = useMyChannels();

    // Don't show the channel selection dropdown if there are no channels or
    // only one channel
    const { channels: allChannels } = useAllChannels();
    const allChannelsCount = allChannels.length;
    if (allChannelsCount <= 1) {
        // console.debug("Hide channel, due to channel number <= 1")
        return;
    }

    function onSwitchChannel(channelKey: string) {
        fireAndForget(setCurrentChannelAsync({ channelKey }));
    }

    const options =
        userChannels.map((channel) => {
            return {
                text: channel.name,
                key: channel.channelKey,
            };
        }) || [];

    if (!(currentChannel ?? options[0]?.key)) {
        return (
            <UtilityText
                label={t('channels.not_channel_member_warning', 'You are not a channel member, this disables certain functions!')}
            />
        );
    }

    return (
        options.length > 0 && (
            <>
                <View style={s.channelDropDownStyle}>
                    <UtilityText label={t('channels.channels_label', 'Channels')} weight='strong' />
                    <DropDownSelector
                        onChange={(channelKey) => onSwitchChannel(channelKey)}
                        options={options}
                        testID='Channels'
                        value={currentChannel?.channelKey ?? options[0].key}
                    />
                </View>
            </>
        )
    );
}
